import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ vendor_id: '', purchase_date: new Date().toISOString().split('T')[0], invoice_no: '', subtotal: 0, tax: 0, discount: 0, total: 0, notes: '', status: 'draft', items: [] });

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [pRes, vRes, prRes] = await Promise.all([api.get('/purchases/list.php'), api.get('/vendors/list.php'), api.get('/products/list.php?limit=200')]);
      if (pRes.data.success) setPurchases(pRes.data.data || []);
      else setError(pRes.data.message);
      if (vRes.data.success) setVendors(vRes.data.data || []);
      if (prRes.data.success) setProducts(prRes.data.data?.products || prRes.data.data || []);
    } catch (e) { setError('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const calcTotals = (items, disc, tx) => {
    const sub = items.reduce((s, i) => s + (parseFloat(i.quantity || 0) * parseFloat(i.rate || 0)), 0);
    const d = parseFloat(disc || 0);
    const t = parseFloat(tx || 0);
    return { subtotal: sub, total: sub + t - d };
  };

  const addItem = () => {
    const newItems = [...form.items, { product_id: '', quantity: 1, rate: 0 }];
    const { subtotal, total } = calcTotals(newItems, form.discount, form.tax);
    setForm({ ...form, items: newItems, subtotal, total });
  };

  const updateItem = (idx, field, val) => {
    const newItems = form.items.map((item, i) => i === idx ? { ...item, [field]: val } : item);
    const { subtotal, total } = calcTotals(newItems, form.discount, form.tax);
    setForm({ ...form, items: newItems, subtotal, total });
  };

  const removeItem = (idx) => {
    const newItems = form.items.filter((_, i) => i !== idx);
    const { subtotal, total } = calcTotals(newItems, form.discount, form.tax);
    setForm({ ...form, items: newItems, subtotal, total });
  };

  const recalc = (field, val) => {
    const { subtotal, total } = calcTotals(form.items, field === 'discount' ? val : form.discount, field === 'tax' ? val : form.tax);
    setForm({ ...form, [field]: val, subtotal, total });
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ vendor_id: form.vendor_id || '', purchase_date: new Date().toISOString().split('T')[0], invoice_no: '', subtotal: 0, tax: 0, discount: 0, total: 0, notes: '', status: 'draft', items: [] });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      vendor_id: p.vendor_id || '',
      purchase_date: (p.purchase_date || '').split(' ')[0] || p.purchase_date || new Date().toISOString().split('T')[0],
      invoice_no: p.invoice_no || '',
      subtotal: parseFloat(p.subtotal || 0),
      tax: parseFloat(p.tax || 0),
      discount: parseFloat(p.discount || 0),
      total: parseFloat(p.total || 0),
      notes: p.notes || '',
      status: p.status || 'draft',
      items: (p.items || []).map(i => ({ product_id: i.product_id, quantity: i.quantity, rate: i.rate, id: i.id }))
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, vendor_id: parseInt(form.vendor_id), subtotal: parseFloat(form.subtotal), tax: parseFloat(form.tax), discount: parseFloat(form.discount), total: parseFloat(form.total), items: form.items.map(i => ({ product_id: parseInt(i.product_id), quantity: parseInt(i.quantity), rate: parseFloat(i.rate) })) };
      if (editing) { await api.post('/purchases/update.php', { id: editing.id, ...payload }); }
      else { await api.post('/purchases/create.php', payload); }
      setShowForm(false);
      fetchAll();
    } catch (e) { setError('Failed to save purchase'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this purchase?')) return;
    try { await api.post('/purchases/delete.php', { id }); fetchAll(); }
    catch (e) { setError('Failed to delete purchase'); }
  };

  const statusColor = (s) => {
    const map = { draft: 'bg-gray-100 text-gray-800', pending: 'bg-yellow-100 text-yellow-800', received: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };
    return map[s?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Purchases</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage purchase orders from vendors: {purchases.length} total</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">New Purchase</button>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {purchases.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No purchases found.</td></tr>
              ) : purchases.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 text-sm font-medium text-blue-600">{p.invoice_no || `#${p.id}`}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{p.vendor_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{p.purchase_date}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{p.items ? p.items.length : 0}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-green-600">₹{parseFloat(p.total || 0).toFixed(2)}</td>
                  <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor(p.status)}`}>{p.status}</span></td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button onClick={() => openEdit(p)} className="text-primary-600 hover:text-primary-900">Edit</button>
                    <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Purchase' : 'New Purchase'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Vendor *</label>
              <select value={form.vendor_id} onChange={e => setForm({...form, vendor_id: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                <option value="">Select Vendor</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Invoice No</label><input type="text" value={form.invoice_no} onChange={e => setForm({...form, invoice_no: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Date</label><input type="date" value={form.purchase_date} onChange={e => setForm({...form, purchase_date: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Items</h3>
              <button type="button" onClick={addItem} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-sm rounded-lg">+ Add Item</button>
            </div>
            {form.items.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">No items added yet. Click "Add Item" to add products.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full mb-3">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b">
                      <th className="px-2 py-2 text-left">Product</th>
                      <th className="px-2 py-2 text-center w-24">Qty</th>
                      <th className="px-2 py-2 text-right w-28">Rate</th>
                      <th className="px-2 py-2 text-right w-28">Amount</th>
                      <th className="px-2 py-2 text-center w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="px-2 py-1.5">
                          <select value={item.product_id} onChange={e => updateItem(idx, 'product_id', e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" required>
                            <option value="">Select</option>
                            {products.filter(p => p.parent_id).length > 0 && (
                              <optgroup label="Variants">
                                {products.filter(p => p.parent_id).map(p => {
                                  const parent = products.find(x => String(x.id) === String(p.parent_id));
                                  const label = parent ? `${parent.name} > ${p.name}` : p.name;
                                  return <option key={p.id} value={p.id}>{label}</option>;
                                })}
                              </optgroup>
                            )}
                            {products.filter(p => !p.parent_id).length > 0 && (
                              <optgroup label="Main Products">
                                {products.filter(p => !p.parent_id).map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                        </td>
                        <td className="px-2 py-1.5"><input type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-center" required /></td>
                        <td className="px-2 py-1.5"><input type="number" step="0.01" min="0" value={item.rate} onChange={e => updateItem(idx, 'rate', e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right" required /></td>
                        <td className="px-2 py-1.5 text-right text-sm font-medium">₹{(parseFloat(item.quantity || 0) * parseFloat(item.rate || 0)).toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-center"><button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 text-sm">&times;</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-200 pt-4">
            <div><label className="block text-sm font-medium mb-1">Subtotal</label><input type="number" step="0.01" value={form.subtotal.toFixed(2)} readOnly className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm" /></div>
            <div><label className="block text-sm font-medium mb-1">Tax (+)</label><input type="number" step="0.01" min="0" value={form.tax} onChange={e => recalc('tax', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm" /></div>
            <div><label className="block text-sm font-medium mb-1">Discount (-)</label><input type="number" step="0.01" min="0" value={form.discount} onChange={e => recalc('discount', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm" /></div>
            <div><label className="block text-sm font-medium mb-1">Total</label><input type="number" step="0.01" value={form.total.toFixed(2)} readOnly className="w-full px-4 py-2 border border-gray-200 bg-green-50 font-bold text-green-800 rounded-lg text-sm" /></div>
            <div><label className="block text-sm font-medium mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="draft">Draft</option><option value="pending">Pending</option><option value="received">Received</option><option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="md:col-span-3"><label className="block text-sm font-medium mb-1">Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows="2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"></textarea></div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editing ? 'Update' : 'Create'} Purchase</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Purchases;
